// src/components/admin/AdminUserManagement.js
import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import axios from 'axios';
import { userService, API_BASE_URL } from '../../services/api';
import { FaEdit, FaTrash, FaPlus, FaUserShield, FaUser, FaSearch, FaFileExport, FaChalkboardTeacher } from 'react-icons/fa';
import {
  ADMIN_COLORS,
  AdminButton, AdminActionBtn,
  AdminTable, AdminTh, AdminTd,
  AdminModalOverlay, AdminModalContent, AdminModalTitle, AdminModalActions,
  AdminFormGroup, AdminLabel, AdminInput, AdminSelect,
  AdminPagination, AdminLoading, AdminCard, AdminTopBar
} from './AdminShared';

const ROLE_COLORS = {
  admin: { bg: '#e3f2fd', color: '#0d47a1' },
  mentor: { bg: '#fff3e0', color: '#e65100' },
  vozama: { bg: '#f3e5f5', color: '#6a1b9a' },
};
const DEFAULT_ROLE_COLOR = { bg: '#f1f8e9', color: '#33691e' };
const ucfirst = (s) => s ? s.charAt(0).toUpperCase() + s.slice(1) : '';

const SearchContainer = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 1.5rem;
  gap: 1rem;
  flex-wrap: wrap;
`;

const SearchInput = styled.div`
  position: relative;
  flex: 1;
  max-width: 300px;

  input {
    width: 100%;
    padding: 0.5rem 0.5rem 0.5rem 2rem;
    border: 1px solid #d1d5db;
    border-radius: 8px;
    font-size: 0.9rem;
    &:focus { outline: none; border-color: ${ADMIN_COLORS.accent}; }
  }

  svg {
    position: absolute;
    left: 0.5rem;
    top: 50%;
    transform: translateY(-50%);
    color: ${ADMIN_COLORS.textLight};
  }
`;

const FilterContainer = styled.div`
  display: flex;
  gap: 1rem;

  select {
    padding: 0.5rem;
    border: 1px solid #d1d5db;
    border-radius: 8px;
    background: white;
    font-size: 0.9rem;
    &:focus { outline: none; border-color: ${ADMIN_COLORS.accent}; }
  }
`;

const RoleLabel = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  padding: 0.25rem 0.5rem;
  border-radius: 9999px;
  font-size: 0.75rem;
  font-weight: 500;
  background-color: ${props => (ROLE_COLORS[props.role] || DEFAULT_ROLE_COLOR).bg};
  color: ${props => (ROLE_COLORS[props.role] || DEFAULT_ROLE_COLOR).color};
`;

const UserCountInfo = styled.div`
  font-size: 0.85rem;
  color: ${ADMIN_COLORS.textLight};
  margin-right: auto;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;

  h3 { margin: 0; color: ${ADMIN_COLORS.primary}; }
  button {
    background: none;
    border: none;
    font-size: 1.25rem;
    cursor: pointer;
    color: ${ADMIN_COLORS.textLight};
    &:hover { color: ${ADMIN_COLORS.danger}; }
  }
`;

const UserFormGroup = styled.div`
  margin-bottom: 1.25rem;

  label {
    display: block;
    margin-bottom: 0.5rem;
    font-weight: 500;
    color: ${ADMIN_COLORS.text};
  }

  input, select {
    width: 100%;
    padding: 0.6rem;
    border: 1px solid #d1d5db;
    border-radius: 8px;
    font-size: 0.9rem;

    &:focus {
      outline: none;
      border-color: ${ADMIN_COLORS.accent};
      box-shadow: 0 0 0 2px rgba(20,182,203,0.15);
    }
  }

  .error {
    color: ${ADMIN_COLORS.danger};
    font-size: 0.75rem;
    margin-top: 0.3rem;
  }
`;

const AdminUserManagement = () => {
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [usersPerPage] = useState(10);
  const [selectedUser, setSelectedUser] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  const [availableRoles, setAvailableRoles] = useState([]);

  const [formData, setFormData] = useState({
    nombre: '',
    email: '',
    password: '',
    role: 'user'
  });

  const [formErrors, setFormErrors] = useState({});

  useEffect(() => {
    const fetchRoles = async () => {
      try {
        const res = await axios.get(`${API_BASE_URL}/roles.php`);
        setAvailableRoles(res.data.roles || []);
      } catch (error) {
        console.error('Error cargando roles:', error);
      }
    };
    fetchRoles();
  }, []);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        setLoading(true);
        const response = await userService.getAll();
        setUsers(response.data);
        setFilteredUsers(response.data);
        setLoading(false);
      } catch (error) {
        console.error('Error cargando usuarios:', error);
        setLoading(false);
      }
    };

    fetchUsers();
  }, []);

  useEffect(() => {
    let filtered = users;

    if (search) {
      filtered = filtered.filter(user =>
        user.nombre.toLowerCase().includes(search.toLowerCase()) ||
        user.email.toLowerCase().includes(search.toLowerCase())
      );
    }

    if (roleFilter) {
      filtered = filtered.filter(user => user.role === roleFilter);
    }

    setFilteredUsers(filtered);
    setCurrentPage(1);
  }, [search, roleFilter, users]);

  const indexOfLastUser = currentPage * usersPerPage;
  const indexOfFirstUser = indexOfLastUser - usersPerPage;
  const currentUsers = filteredUsers.slice(indexOfFirstUser, indexOfLastUser);
  const totalPages = Math.ceil(filteredUsers.length / usersPerPage);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const handleEdit = (user) => {
    setSelectedUser(user);
    setFormData({
      nombre: user.nombre,
      email: user.email,
      password: '',
      role: user.role
    });
    setIsModalOpen(true);
  };

  const handleDelete = (user) => {
    setSelectedUser(user);
    setIsDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    try {
      await userService.deleteUser(selectedUser.id);
      setUsers(users.filter(user => user.id !== selectedUser.id));
      setIsDeleteModalOpen(false);
      setSelectedUser(null);
    } catch (error) {
      console.error('Error eliminando usuario:', error);
    }
  };

  const handleAddNew = () => {
    setSelectedUser(null);
    setFormData({
      nombre: '',
      email: '',
      password: '',
      role: 'user'
    });
    setFormErrors({});
    setIsModalOpen(true);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });

    if (formErrors[name]) {
      setFormErrors({
        ...formErrors,
        [name]: null
      });
    }
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.nombre.trim()) {
      errors.nombre = 'El nombre es obligatorio';
    }

    if (!formData.email.trim()) {
      errors.email = 'El email es obligatorio';
    } else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(formData.email)) {
      errors.email = 'Email inválido';
    }

    if (!selectedUser && !formData.password) {
      errors.password = 'La contraseña es obligatoria';
    } else if (formData.password && formData.password.length < 6) {
      errors.password = 'La contraseña debe tener al menos 6 caracteres';
    }

    return errors;
  };

  const handleSubmit = async () => {
    const errors = validateForm();

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    try {
      if (selectedUser) {
        const updatedUser = {
          id: selectedUser.id,
          ...formData
        };
        await userService.updateUser(updatedUser);

        setUsers(users.map(user =>
          user.id === selectedUser.id ? { ...user, ...formData } : user
        ));
      } else {
        const response = await userService.createUser(formData);
        setUsers([...users, response.data]);
      }

      setIsModalOpen(false);
    } catch (error) {
      console.error('Error guardando usuario:', error);

      if (error.response && error.response.data && error.response.data.message === 'El email ya está registrado') {
        setFormErrors({
          ...formErrors,
          email: 'El email ya está registrado'
        });
      }
    }
  };

  const exportToCSV = () => {
    const escapeCSV = (val) => {
      if (!val) return '';
      const str = String(val);
      return str.includes(',') || str.includes('"') ? `"${str.replace(/"/g, '""')}"` : str;
    };
    const csvContent = [
      ['ID', 'Nombre', 'Email', 'Rol', 'Teléfono', 'Código País', 'Convenio', 'Especialidad', 'País', 'DNI', 'Fecha de Creación'].join(','),
      ...filteredUsers.map(user =>
        [user.id, escapeCSV(user.nombre), escapeCSV(user.email), user.role,
         escapeCSV(user.telefono), escapeCSV(user.codigo_pais), escapeCSV(user.convenio_nombre),
         escapeCSV(user.especialidad), escapeCSV(user.pais), escapeCSV(user.dni), user.created].join(',')
      )
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `usuarios_voicemed_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <AdminCard style={{ padding: '1.5rem' }}>
      <AdminTopBar>
        <h2 style={{ margin: 0, color: ADMIN_COLORS.primary }}>Gestión de Usuarios</h2>
        <div style={{ display: 'flex', gap: '1rem' }}>
          <AdminButton $ghost onClick={exportToCSV}>
            <FaFileExport />
            Exportar
          </AdminButton>
          <AdminButton onClick={handleAddNew}>
            <FaPlus />
            Nuevo Usuario
          </AdminButton>
        </div>
      </AdminTopBar>

      <SearchContainer>
        <SearchInput>
          <FaSearch />
          <input
            type="text"
            placeholder="Buscar usuarios..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </SearchInput>

        <FilterContainer>
          <select
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value)}
          >
            <option value="">Todos los roles</option>
            {availableRoles.map(r => (
              <option key={r.name} value={r.name}>{r.label}</option>
            ))}
          </select>
        </FilterContainer>
      </SearchContainer>

      {loading ? (
        <AdminLoading>Cargando usuarios...</AdminLoading>
      ) : (
        <>
          <AdminTable>
            <thead>
              <tr>
                <AdminTh>ID</AdminTh>
                <AdminTh>Nombre</AdminTh>
                <AdminTh>Email</AdminTh>
                <AdminTh>Rol</AdminTh>
                <AdminTh>Teléfono</AdminTh>
                <AdminTh>Convenio</AdminTh>
                <AdminTh>Especialidad</AdminTh>
                <AdminTh>País</AdminTh>
                <AdminTh>DNI</AdminTh>
                <AdminTh>Fecha Creación</AdminTh>
                <AdminTh>Acciones</AdminTh>
              </tr>
            </thead>
            <tbody>
              {currentUsers.length === 0 ? (
                <tr>
                  <AdminTd colSpan="11" style={{ textAlign: 'center' }}>No se encontraron usuarios</AdminTd>
                </tr>
              ) : (
                currentUsers.map(user => (
                  <tr key={user.id}>
                    <AdminTd>{user.id}</AdminTd>
                    <AdminTd>{user.nombre}</AdminTd>
                    <AdminTd>{user.email}</AdminTd>
                    <AdminTd>
                      <RoleLabel role={user.role}>
                        {user.role === 'admin' ? <FaUserShield /> : user.role === 'mentor' ? <FaChalkboardTeacher /> : <FaUser />}
                        {(availableRoles.find(r => r.name === user.role) || {}).label || ucfirst(user.role)}
                      </RoleLabel>
                    </AdminTd>
                    <AdminTd style={{ fontSize: '0.82rem' }}>
                      {user.telefono ? `${user.codigo_pais || ''} ${user.telefono}` : '—'}
                    </AdminTd>
                    <AdminTd style={{ fontSize: '0.82rem' }}>{user.convenio_nombre || '—'}</AdminTd>
                    <AdminTd style={{ fontSize: '0.82rem' }}>{user.especialidad || '—'}</AdminTd>
                    <AdminTd style={{ fontSize: '0.82rem' }}>{user.pais || '—'}</AdminTd>
                    <AdminTd style={{ fontSize: '0.82rem' }}>{user.dni || '—'}</AdminTd>
                    <AdminTd>{new Date(user.created).toLocaleDateString()}</AdminTd>
                    <AdminTd>
                      <div style={{ display: 'flex', gap: '0.5rem' }}>
                        <AdminActionBtn onClick={() => handleEdit(user)}>
                          <FaEdit />
                        </AdminActionBtn>
                        <AdminActionBtn $danger onClick={() => handleDelete(user)}>
                          <FaTrash />
                        </AdminActionBtn>
                      </div>
                    </AdminTd>
                  </tr>
                ))
              )}
            </tbody>
          </AdminTable>

          <AdminPagination>
            <UserCountInfo>
              Mostrando {currentUsers.length} de {filteredUsers.length} usuarios
            </UserCountInfo>

            <button
              onClick={() => paginate(1)}
              disabled={currentPage === 1}
            >
              &laquo;
            </button>

            <button
              onClick={() => paginate(currentPage - 1)}
              disabled={currentPage === 1}
            >
              &lsaquo;
            </button>

            {Array.from({ length: Math.min(5, totalPages) }).map((_, index) => {
              let pageToShow;
              if (totalPages <= 5) {
                pageToShow = index + 1;
              } else if (currentPage <= 3) {
                pageToShow = index + 1;
              } else if (currentPage >= totalPages - 2) {
                pageToShow = totalPages - 4 + index;
              } else {
                pageToShow = currentPage - 2 + index;
              }

              return (
                <button
                  key={index}
                  onClick={() => paginate(pageToShow)}
                  className={currentPage === pageToShow ? 'active' : ''}
                >
                  {pageToShow}
                </button>
              );
            })}

            <button
              onClick={() => paginate(currentPage + 1)}
              disabled={currentPage === totalPages}
            >
              &rsaquo;
            </button>

            <button
              onClick={() => paginate(totalPages)}
              disabled={currentPage === totalPages}
            >
              &raquo;
            </button>
          </AdminPagination>
        </>
      )}

      {isModalOpen && (
        <AdminModalOverlay>
          <AdminModalContent>
            <ModalHeader>
              <h3>{selectedUser ? 'Editar Usuario' : 'Nuevo Usuario'}</h3>
              <button onClick={() => setIsModalOpen(false)}>&times;</button>
            </ModalHeader>

            <form onSubmit={(e) => { e.preventDefault(); handleSubmit(); }}>
              <UserFormGroup>
                <label htmlFor="nombre">Nombre</label>
                <input
                  id="nombre"
                  name="nombre"
                  type="text"
                  value={formData.nombre}
                  onChange={handleInputChange}
                />
                {formErrors.nombre && <div className="error">{formErrors.nombre}</div>}
              </UserFormGroup>

              <UserFormGroup>
                <label htmlFor="email">Email</label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleInputChange}
                />
                {formErrors.email && <div className="error">{formErrors.email}</div>}
              </UserFormGroup>

              <UserFormGroup>
                <label htmlFor="password">
                  Contraseña {selectedUser && '(Dejar en blanco para mantener la actual)'}
                </label>
                <input
                  id="password"
                  name="password"
                  type="password"
                  value={formData.password}
                  onChange={handleInputChange}
                />
                {formErrors.password && <div className="error">{formErrors.password}</div>}
              </UserFormGroup>

              <UserFormGroup>
                <label htmlFor="role">Rol</label>
                <select
                  id="role"
                  name="role"
                  value={formData.role}
                  onChange={handleInputChange}
                >
                  {availableRoles.map(r => (
                    <option key={r.name} value={r.name}>{r.label}</option>
                  ))}
                </select>
              </UserFormGroup>

              <AdminModalActions>
                <AdminButton $ghost type="button" onClick={() => setIsModalOpen(false)}>Cancelar</AdminButton>
                <AdminButton type="submit">{selectedUser ? 'Actualizar' : 'Crear'}</AdminButton>
              </AdminModalActions>
            </form>
          </AdminModalContent>
        </AdminModalOverlay>
      )}

      {isDeleteModalOpen && (
        <AdminModalOverlay>
          <AdminModalContent>
            <ModalHeader>
              <h3>Confirmar Eliminación</h3>
              <button onClick={() => setIsDeleteModalOpen(false)}>&times;</button>
            </ModalHeader>

            <p>¿Estás seguro de eliminar al usuario <strong>{selectedUser?.nombre}</strong>?</p>
            <p>Esta acción no se puede deshacer.</p>

            <AdminModalActions>
              <AdminButton $ghost onClick={() => setIsDeleteModalOpen(false)}>Cancelar</AdminButton>
              <AdminButton $danger onClick={confirmDelete}>Eliminar</AdminButton>
            </AdminModalActions>
          </AdminModalContent>
        </AdminModalOverlay>
      )}
    </AdminCard>
  );
};

export default AdminUserManagement;
